import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  Activity,
  Competency,
  ApiLearnerOutcome,
  LearnerOutcome,
  OutcomeArea,
  SubCompetency,
} from "@/lib/models";
import {
  getPerformanceLevelHierarchyFromId,
  normalizeEntities,
} from "@/lib/utils";

export type FrameworkHierarchy = {
  activity?: Activity;
  learnerOutcome?: LearnerOutcome;
  outcomeArea?: OutcomeArea;
  competency?: Competency;
  subCompetency?: SubCompetency;
  performanceLevel?: {
    id: string;
    name: string;
  };
};

type EntitiesContextState = EntitiesState & {
  getEntityHierarchyFromId: (id: string) => FrameworkHierarchy;
  getEntityHierarchyFromActivitySlug: (
    slug: string,
  ) => FrameworkHierarchy & { activity?: Activity };
  getLearnerOutcome: (id: string) => LearnerOutcome | undefined;
  getOutcomeArea: (id: string) => OutcomeArea | undefined;
  getCompetency: (id: string) => Competency | undefined;
  getSubCompetency: (id: string) => SubCompetency | undefined;
  getActivity: (slug: string) => Activity | undefined;
  getActivitiesForCompetency: (id: string) => Activity[];
  getActivitiesForOutcomeArea: (id: string) => Activity[];
  getCompetenciesForLearnerOutcome: (id: string) => Competency[];
};

export const EntitiesContext = createContext<EntitiesContextState>({
  learnerOutcomes: [],
  outcomeAreas: [],
  competencies: [],
  subCompetencies: [],
  activities: [],
  getEntityHierarchyFromId: () => ({}),
  getEntityHierarchyFromActivitySlug: () => ({}),
  getLearnerOutcome: () => undefined,
  getOutcomeArea: () => undefined,
  getCompetency: () => undefined,
  getSubCompetency: () => undefined,
  getActivity: () => undefined,
  getActivitiesForCompetency: () => [],
  getActivitiesForOutcomeArea: () => [],
  getCompetenciesForLearnerOutcome: () => [],
});

type EntitiesProviderProps = {
  children: ReactNode;
  learnerOutcomes: ApiLearnerOutcome[];
};

const EntitiesProvider = ({
  children,
  learnerOutcomes,
}: EntitiesProviderProps) => {
  const entities = useMemo(
    () => normalizeEntities(learnerOutcomes),
    [learnerOutcomes],
  );

  const entitiesHashMap: EntitiesMap = useMemo(
    () =>
      Object.keys(entities).reduce((hashMap, entityType) => {
        const entityArray: any[] = entities[entityType as keyof EntitiesState];
        const entityMap = entityArray.reduce(
          (acc, entity) => ({ ...acc, [entity.id || entity.urlSlug]: entity }),
          {},
        );

        return {
          ...hashMap,
          [entityType]: entityMap,
        };
      }, {} as EntitiesMap),
    [entities],
  );

  const getEntityHierarchyFromId = useCallback(
    (id: string): FrameworkHierarchy => {
      const performanceLevelIds = entities.subCompetencies.flatMap(
        (subCompetency) => [
          subCompetency.performanceLevel1Id,
          subCompetency.performanceLevel2Id,
          subCompetency.performanceLevel3Id,
          subCompetency.performanceLevel4Id,
        ],
      );

      return {
        learnerOutcome: entities.learnerOutcomes.find((lo) =>
          id.startsWith(lo.id),
        ),
        outcomeArea: entities.outcomeAreas.find((oa) => id.startsWith(oa.id)),
        competency: entities.competencies.find((c) => id.startsWith(c.id)),
        subCompetency: entities.subCompetencies.find((sc) =>
          id.startsWith(sc.id),
        ),
        performanceLevel: performanceLevelIds.includes(id)
          ? {
              id,
              name: getPerformanceLevelHierarchyFromId(id),
            }
          : undefined,
      };
    },
    [entities],
  );

  const getEntityHierarchyFromActivitySlug = (slug: string) => {
    const subcompetency = entities.subCompetencies.find((sc) =>
      sc.activities.some((activity) => activity.urlSlug === slug),
    );

    if (!subcompetency) return {};

    return {
      ...getEntityHierarchyFromId(subcompetency.id),
      activity: subcompetency.activities.find(
        (activity) => activity.urlSlug === slug,
      ),
    };
  };

  const getLearnerOutcome = (id: string) => entitiesHashMap.learnerOutcomes[id];
  const getOutcomeArea = (id: string) => entitiesHashMap.outcomeAreas[id];
  const getCompetency = (id: string) => entitiesHashMap.competencies[id];
  const getSubCompetency = (id: string) => entitiesHashMap.subCompetencies[id];
  const getActivity = (slug: string) => entitiesHashMap.activities[slug];

  const getActivitiesForCompetency = (competencyId: string) => {
    const competency = getCompetency(competencyId)!;
    const subCompetencies = competency.subCompetencies.map(getSubCompetency);

    return subCompetencies.flatMap((sc) => sc.activities);
  };

  const getActivitiesForOutcomeArea = (outcomeAreaId: string) => {
    const outcomeArea = getOutcomeArea(outcomeAreaId)!;
    const competencies = outcomeArea.competencies.map(getCompetency);
    const subCompetencies = competencies.flatMap((competency) => {
      return competency.subCompetencies.map(getSubCompetency);
    });

    return subCompetencies.flatMap((sc) => sc.activities);
  };

  const getCompetenciesForLearnerOutcome = (learnerOutcomeId: string) => {
    const learnerOutcome = getLearnerOutcome(learnerOutcomeId)!;
    const outcomeAreas = learnerOutcome.outcomeAreas.map(getOutcomeArea);

    return outcomeAreas.flatMap((oa) => oa.competencies.map(getCompetency));
  };

  return (
    <EntitiesContext.Provider
      value={{
        learnerOutcomes: entities.learnerOutcomes,
        outcomeAreas: entities.outcomeAreas,
        competencies: entities.competencies,
        subCompetencies: entities.subCompetencies,
        activities: entities.activities,
        getEntityHierarchyFromId,
        getEntityHierarchyFromActivitySlug,
        getLearnerOutcome,
        getOutcomeArea,
        getCompetency,
        getSubCompetency,
        getActivity,
        getActivitiesForCompetency,
        getActivitiesForOutcomeArea,
        getCompetenciesForLearnerOutcome,
      }}
    >
      {children}
    </EntitiesContext.Provider>
  );
};

export default EntitiesProvider;

export const useEntities = () => {
  return useContext(EntitiesContext);
};

export type EntitiesState = {
  learnerOutcomes: LearnerOutcome[];
  outcomeAreas: OutcomeArea[];
  competencies: Competency[];
  subCompetencies: SubCompetency[];
  activities: Activity[];
};

type EntityMap<T> = {
  [id: string]: T;
};

type EntitiesMap = {
  [K in keyof EntitiesState]: EntityMap<EntitiesState[K][0]>;
};
